import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  getPublicLeagues,
  joinLeague,
  isLeagueMember,
} from '../services/leagueService';
import './LeagueDirectory.css';

export default function LeagueDirectory() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [leagues, setLeagues] = useState(null);
  const [memberMap, setMemberMap] = useState({}); // leagueId → boolean
  const [commissionerNames, setCommissionerNames] = useState({}); // uid → username
  const [joiningId, setJoiningId] = useState(null);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  // Load directory + membership status for current user
  const loadDirectory = useCallback(async () => {
    if (!currentUser) return;
    setError('');
    try {
      const data = await getPublicLeagues();
      setLeagues(data);

      // Check membership for each league in parallel
      const memberResults = await Promise.all(
        data.map((l) => isLeagueMember(l.id, currentUser.uid))
      );
      const map = {};
      data.forEach((l, i) => { map[l.id] = memberResults[i]; });
      setMemberMap(map);

      // Fetch commissioner display names (deduplicate)
      const uniqueUids = [...new Set(data.map((l) => l.commissionerId))];
      const nameResults = await Promise.all(
        uniqueUids.map(async (uid) => {
          const snap = await getDoc(doc(db, 'users', uid));
          return [uid, snap.exists() ? snap.data().username : 'Unknown'];
        })
      );
      setCommissionerNames(Object.fromEntries(nameResults));
    } catch (_) {
      setError('Failed to load the directory. Please try again.');
      setLeagues([]);
    }
  }, [currentUser]);

  useEffect(() => { loadDirectory(); }, [loadDirectory]);

  async function handleJoin(leagueId, leagueName) {
    setJoiningId(leagueId);
    try {
      await joinLeague(leagueId, currentUser.uid);
      setMemberMap((prev) => ({ ...prev, [leagueId]: true }));
      // Update memberCount optimistically
      setLeagues((prev) =>
        prev.map((l) =>
          l.id === leagueId ? { ...l, memberCount: l.memberCount + 1 } : l
        )
      );
      showToast(`Joined "${leagueName}"!`);
    } catch (_) {
      setError('Failed to join league. Please try again.');
    } finally {
      setJoiningId(null);
    }
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }

  function formatDate(ts) {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  return (
    <div className="directory-page">
      {/* Header */}
      <header className="directory-header">
        <button className="directory-header-back" onClick={() => navigate('/leagues')}>
          ← Back
        </button>
        <span className="directory-header-title">Public Leagues</span>
      </header>

      <div className="directory-body">
        <p className="directory-subtitle">
          Join any public league with one tap.
        </p>

        {error && (
          <div className="directory-error">
            {error}
            <button className="directory-retry" onClick={loadDirectory}>Retry</button>
          </div>
        )}

        {leagues === null && <div className="directory-loading">Loading leagues…</div>}

        {leagues && leagues.length === 0 && (
          <div className="directory-empty">No public leagues yet. Be the first to create one!</div>
        )}

        {leagues && leagues.map((league) => {
          const alreadyMember = memberMap[league.id];
          const commName = commissionerNames[league.commissionerId] || '…';
          const isJoining = joiningId === league.id;

          return (
            <div className="dir-card" key={league.id}>
              <div className="dir-card-info">
                <div className="dir-card-name">{league.name}</div>
                <div className="dir-card-meta">
                  <span>by {commName}</span>
                  <span className="dir-card-dot">·</span>
                  <span>{league.memberCount} member{league.memberCount !== 1 ? 's' : ''}</span>
                  <span className="dir-card-dot">·</span>
                  <span>Created {formatDate(league.createdAt)}</span>
                </div>
              </div>

              {alreadyMember ? (
                <button
                  className="dir-card-btn dir-card-btn--member"
                  disabled
                  aria-label="Already a member"
                >
                  ✓ Joined
                </button>
              ) : (
                <button
                  className="dir-card-btn dir-card-btn--join"
                  disabled={isJoining}
                  onClick={() => handleJoin(league.id, league.name)}
                  aria-label={`Join ${league.name}`}
                >
                  {isJoining ? 'Joining…' : 'Join'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Success toast */}
      {toast && <div className="directory-toast">{toast}</div>}
    </div>
  );
}
